import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  LinearProgress,
  Chip,
  TextField,
  Tooltip,
  Alert,
  Collapse,
  IconButton,
} from '@mui/material';
import { Person, ExpandMore, ExpandLess, Circle } from '@mui/icons-material';
import api from '../../services/api';
import type { StrategicClientsReportResponse, StrategicClientReport as StrategicClientReportType, StrategicClientTimelineEntry } from '../../types';

const colorMap: Record<string, string> = {
  green: '#4caf50',   // Pagado
  yellow: '#ff9800',  // Pendiente de pago
  violet: '#9c27b0',  // Cheque entregado no acreditado
  gray: '#9e9e9e',    // Proyección futura
};

const colorLabels: Record<string, string> = {
  green: 'Pagado',
  yellow: 'Pendiente',
  violet: 'Cheque pendiente',
  gray: 'Proyectado',
};

interface TimelineBarProps {
  entries: StrategicClientTimelineEntry[];
  totalAmount: number;
}

const TimelineBar: React.FC<TimelineBarProps> = ({ entries, totalAmount }) => {
  if (entries.length === 0 || totalAmount === 0) {
    return (
      <Box sx={{ height: 24, bgcolor: 'grey.200', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography variant="caption" color="text.secondary">Sin registros</Typography>
      </Box>
    );
  }

  // Group entries by color and calculate percentages
  const colorGroups: Record<string, number> = {};
  entries.forEach(e => {
    const color = e.color || 'gray';
    colorGroups[color] = (colorGroups[color] || 0) + e.amount;
  });

  const segments = Object.entries(colorGroups).map(([color, amount]) => ({
    color,
    amount,
    percentage: (amount / totalAmount) * 100,
  }));

  // Sort: green first, then yellow, violet, gray
  const colorOrder = ['green', 'yellow', 'violet', 'gray'];
  segments.sort((a, b) => colorOrder.indexOf(a.color) - colorOrder.indexOf(b.color));

  return (
    <Stack direction="row" sx={{ height: 24, borderRadius: 1, overflow: 'hidden' }}>
      {segments.map((seg, idx) => (
        <Tooltip
          key={idx}
          title={`${colorLabels[seg.color] || seg.color}: $${seg.amount.toLocaleString()} (${seg.percentage.toFixed(1)}%)`}
        >
          <Box
            sx={{
              width: `${seg.percentage}%`,
              minWidth: seg.percentage > 0 ? 4 : 0,
              bgcolor: colorMap[seg.color] || '#9e9e9e',
              height: '100%',
              transition: 'all 0.3s',
              '&:hover': { opacity: 0.8 },
            }}
          />
        </Tooltip>
      ))}
    </Stack>
  );
};

interface ClientCardProps {
  client: StrategicClientReportType;
}

const ClientCard: React.FC<ClientCardProps> = ({ client }) => {
  const [expanded, setExpanded] = useState(false);
  const totalAmount = client.totalPaid + client.totalPending + client.totalProjected;

  const formatCurrency = (val: number) => `$${val.toLocaleString()}`;

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent sx={{ pb: expanded ? 2 : 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Person color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>{client.customerName}</Typography>
            {client.customerType && <Chip size="small" label={client.customerType} variant="outlined" />}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Saldo: <strong>{formatCurrency(client.currentBalance)}</strong>
            </Typography>
            <IconButton size="small" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Stack>
        </Stack>

        {/* Timeline bar */}
        <TimelineBar entries={client.timeline} totalAmount={totalAmount} />

        {/* Legend */}
        <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Circle sx={{ fontSize: 10, color: colorMap.green }} />
            <Typography variant="caption">{formatCurrency(client.totalPaid)}</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Circle sx={{ fontSize: 10, color: colorMap.yellow }} />
            <Typography variant="caption">{formatCurrency(client.totalPending)}</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Circle sx={{ fontSize: 10, color: colorMap.gray }} />
            <Typography variant="caption">{formatCurrency(client.totalProjected)}</Typography>
          </Stack>
        </Stack>

        {/* Expanded timeline details */}
        <Collapse in={expanded}>
          <Box sx={{ mt: 2, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
            {client.timeline.length === 0 && (
              <Typography variant="body2" color="text.secondary">Sin registros en el período</Typography>
            )}
            {client.timeline.map((entry, idx) => (
              <Stack
                key={`${entry.id}-${idx}`}
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ py: 0.5, borderBottom: idx < client.timeline.length - 1 ? '1px solid' : 'none', borderColor: 'divider' }}
              >
                <Circle sx={{ fontSize: 12, color: colorMap[entry.color] || colorMap.gray }} />
                <Typography variant="body2" sx={{ minWidth: 80 }}>
                  {new Date(entry.date).toLocaleDateString()}
                </Typography>
                <Chip
                  size="small"
                  label={entry.entryType === 'Order' ? 'Orden' : 'Oportunidad'}
                  variant="outlined"
                  sx={{ minWidth: 80 }}
                />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {entry.reference}
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {formatCurrency(entry.amount)}
                </Typography>
                <Chip
                  size="small"
                  label={colorLabels[entry.color] || entry.paymentStatus}
                  sx={{ bgcolor: colorMap[entry.color] || colorMap.gray, color: 'white', minWidth: 90 }}
                />
              </Stack>
            ))}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

const StrategicClientReport: React.FC = () => {
  const [data, setData] = useState<StrategicClientsReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterCustomer, setFilterCustomer] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api.getStrategicClientReport();
      setData(result);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Error al cargar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = data?.clients?.filter(c =>
    !filterCustomer || c.customerName.toLowerCase().includes(filterCustomer.toLowerCase())
  ) || [];

  const formatCurrency = (val: number) => `$${val.toLocaleString()}`;

  // Calculate totals
  const totals = filteredClients.reduce(
    (acc, c) => ({
      paid: acc.paid + c.totalPaid,
      pending: acc.pending + c.totalPending,
      projected: acc.projected + c.totalProjected,
    }),
    { paid: 0, pending: 0, projected: 0 }
  );

  return (
    <Card>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <Person />
          <Typography variant="h6" fontWeight={600}>Clientes Estratégicos</Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Timeline de pagos y proyecciones por cliente. Verde = pagado, Amarillo = pendiente, Violeta = cheque no acreditado, Gris = proyección.
        </Typography>

        {loading && <LinearProgress />}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {!loading && data && (
          <>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField
                size="small"
                label="Filtrar por cliente"
                value={filterCustomer}
                onChange={(e) => setFilterCustomer(e.target.value)}
                sx={{ minWidth: 250 }}
              />
              <Stack direction="row" spacing={2} alignItems="center">
                <Chip
                  label={`${filteredClients.length} clientes`}
                  variant="outlined"
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <Circle sx={{ fontSize: 12, color: colorMap.green }} />
                  <Typography variant="body2">{formatCurrency(totals.paid)}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Circle sx={{ fontSize: 12, color: colorMap.yellow }} />
                  <Typography variant="body2">{formatCurrency(totals.pending)}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Circle sx={{ fontSize: 12, color: colorMap.gray }} />
                  <Typography variant="body2">{formatCurrency(totals.projected)}</Typography>
                </Stack>
              </Stack>
            </Stack>

            {filteredClients.length === 0 ? (
              <Alert severity="info">
                No se encontraron clientes estratégicos. Asegurate de que los clientes tengan el tipo "Estratégico" asignado.
              </Alert>
            ) : (
              filteredClients.map((client) => (
                <ClientCard key={client.customerId} client={client} />
              ))
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default StrategicClientReport;
